import { useState } from 'react';
import {
  mapGetAllRecipientGroupsResponseType,
  mapGetAvailableRecipientsResponseType,
  RecipientGroupType,
  useGetAllRecipientGroupsSuspense,
  useGetAvailableRecipients,
} from '@api';
import {
  getReportMailingBaseFormShape,
  RecipientGroupForm,
  RecipientGroupFormRecipientType,
  RecipientGroupFormValuesType,
  RecipientGroupsTable,
} from '@common';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { Center, Loader, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { Route } from 'src/routes/_rootLayout/venues/$id.reports';
import { z } from 'zod';
import {
  mapFormValuesToUpdateRecipientGroupRequest,
  mapRecipientGroupToFormValues,
} from './helpers';
import {
  useAddRecipientGroup,
  useDeleteRecipientGroup,
  useUpdateRecipientGroup,
} from './hooks';

type AddRecipientGroupFormValuesType = Pick<
  RecipientGroupFormValuesType,
  'label'
>;

export const VenueRecipientGroups: React.FC = () => {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { hasPermission } = usePermissionContext();
  const [openedAdd, { open: openModalAdd, close: closeModalAdd }] =
    useDisclosure(false);
  const [selectedGroup, setSelectedGroup] = useState<RecipientGroupType | null>(
    null
  );
  const { data: groups } = useGetAllRecipientGroupsSuspense(
    { venueId: id },
    {
      query: {
        select: (response) =>
          mapGetAllRecipientGroupsResponseType(response).results,
      },
    }
  );
  // Only needed while editing, so it is loaded when the edit modal opens.
  const { data: availablePersons } = useGetAvailableRecipients(
    { venueId: id },
    {
      query: {
        enabled: selectedGroup !== null,
        select: (response) =>
          mapGetAvailableRecipientsResponseType(response).results,
      },
    }
  );
  const { addRecipientGroup } = useAddRecipientGroup({ venueId: id });
  const { updateRecipientGroup } = useUpdateRecipientGroup();
  const { handleDeleteRecipientGroup } = useDeleteRecipientGroup();

  const canWrite = hasPermission({ permission: 'REPORTS', action: 'WRITE' });
  const { label: labelSchema } = getReportMailingBaseFormShape(t);

  const closeModalEdit = () => setSelectedGroup(null);

  const handleAddRecipientGroup = async ({
    label,
  }: AddRecipientGroupFormValuesType) => {
    if (await addRecipientGroup(label)) {
      closeModalAdd();
    }
  };

  const handleUpdateRecipientGroup = async (
    values: RecipientGroupFormValuesType
  ) => {
    if (!selectedGroup) return;

    if (
      await updateRecipientGroup(
        selectedGroup.id,
        mapFormValuesToUpdateRecipientGroupRequest(values)
      )
    ) {
      closeModalEdit();
    }
  };

  return (
    <>
      <RecipientGroupsTable
        groups={groups}
        canEdit={canWrite}
        onAdd={openModalAdd}
        onEdit={setSelectedGroup}
        onDelete={handleDeleteRecipientGroup}
      />
      <Modal
        opened={openedAdd}
        onClose={closeModalAdd}
        title={t('REPORTS.RECIPIENT_GROUPS.ADD_TITLE')}
      >
        <RecipientGroupForm.Root<AddRecipientGroupFormValuesType>
          schema={z.object({ label: labelSchema })}
          defaultValues={{ label: '' }}
          onSave={handleAddRecipientGroup}
          onCancel={closeModalAdd}
        >
          <RecipientGroupForm.Label<AddRecipientGroupFormValuesType> fieldName="label" />
        </RecipientGroupForm.Root>
      </Modal>
      <Modal
        opened={selectedGroup !== null}
        onClose={closeModalEdit}
        title={t('REPORTS.RECIPIENT_GROUPS.EDIT_TITLE')}
        size="xl"
      >
        {selectedGroup &&
          (availablePersons ? (
            <RecipientGroupForm.Root<RecipientGroupFormValuesType>
              schema={z.object({
                label: labelSchema,
                // Rows are validated when they are added.
                recipients: z.array(
                  z.custom<RecipientGroupFormRecipientType>()
                ),
              })}
              defaultValues={mapRecipientGroupToFormValues(selectedGroup)}
              onSave={handleUpdateRecipientGroup}
              onCancel={closeModalEdit}
            >
              <RecipientGroupForm.Label<RecipientGroupFormValuesType> fieldName="label" />
              <RecipientGroupForm.Recipients
                availablePersons={availablePersons}
              />
            </RecipientGroupForm.Root>
          ) : (
            <Center p="xl">
              <Loader />
            </Center>
          ))}
      </Modal>
    </>
  );
};
